/**
 * stackedEdges — Kanten, deren beide Enden in DERSELBEN Bahn liegen.
 *
 * Von 17 Kanten der Startseite (13 der Architektur) sind genau zwei bahnintern:
 * `maco-energy-scraper → maco-energy-mirror` und
 * `maco-regu-parse → maco-knowledge-collection`. Beide sagen dasselbe — „der
 * eine Knoten ist da, um den anderen zu füllen" — und beide waren faktisch
 * unsichtbar: die Bézier-Führung von `useFlowEdges` läuft vom RECHTEN Rand des
 * Quellknotens zum LINKEN Rand des Ziels. Bei gestapelten Knoten derselben Bahn
 * liegen diese beiden Ränder übereinander; die Kurve schnürt sich auf wenige
 * Pixel zusammen und verschwindet hinter den Knoten.
 *
 * Diese Datei trägt die drei Teile des Ersatzes, ohne DOM-Messung testbar:
 *
 *  1. **Erkennung** (`isStackedPair`) — nicht geometrisch, sondern strukturell:
 *     beide Oberflächen klammern eine Bahn in ein `[role="group"]`
 *     (`.exec-lane` bzw. `.arch-stack`). Zwei Knoten liegen in derselben Bahn,
 *     wenn sie dieselbe Gruppe als nächsten Vorfahren haben. Das gilt in beiden
 *     Anordnungen — auch in der gestapelten, in der nichts gemessen wird — und
 *     es gilt in jsdom, wo es keine Layout-Engine gibt.
 *  2. **Führung** (`stackedEdgePath`) — eine kurze, gerade Strecke auf der
 *     Glyphenachse mit einer PFEILSPITZE am Ziel. Die Richtung ist hier die
 *     ganze Aussage; eine neutrale Linie wäre genau der gemeldete Mangel.
 *  3. **Sprache** (`useFlowRelations`) — dieselbe Beziehung als Wort im
 *     `aria-label` beider Knoten. Geometrie war für Screenreader noch nie
 *     lesbar, und im gestapelten Layout ist das Overlay abgeschaltet.
 */
import { useCallback, useEffect, useRef, type RefObject } from "react";
import type { FlowEdgeSpec } from "./useFlowEdges";

/** Das gemessene Rechteck eines Knotens, relativ zum Canvas. */
export interface EdgeBox {
  left: number;
  right: number;
  top: number;
  bottom: number;
  cx: number;
  cy: number;
}

/**
 * Der `StateGlyph` sitzt 16 px breit am linken Rand des Knotenkopfs — seine
 * Mitte liegt also 8 px hinter der Kante. Die bahninterne Verbindung läuft auf
 * genau dieser Achse: sie verbindet sichtbar die beiden Zustandsformen, nicht
 * zwei beliebige Kastenmitten. Dieselbe Achse trägt in `executive.css` die
 * Fugenlinie zwischen den Bahnen.
 */
const AXIS = 8;
/** Abstand zum Knotenrand, damit die Spitze nicht am Kasten klebt. */
const INSET = 3;
/** Länge und halbe Breite der Pfeilspitze. */
const HEAD = 5;
const HALF = 3.4;

const GROUP_SELECTOR = '[role="group"]';

export type StackDir = "down" | "up" | "right" | "left";

/** Die Bahn eines Knotens als DOM-Element — `null`, wenn er in keiner steht. */
export function flowGroupOf(el: Element | null | undefined): Element | null {
  return el?.closest(GROUP_SELECTOR) ?? null;
}

/**
 * Beide Enden in derselben Bahn? Ein Querschnitts-Balken (`arch-merge`) steht in
 * keiner Gruppe und ist damit nie bahnintern — richtig so, er überspannt sechs.
 */
export function isStackedPair(from: Element | null | undefined, to: Element | null | undefined): boolean {
  const group = flowGroupOf(from);
  return !!group && group === flowGroupOf(to);
}

/**
 * Wohin zeigt die Verbindung? Die dominante Achse gewinnt: breit stehen die
 * Knoten einer Bahn untereinander (`down`), gestapelt nebeneinander (`right`).
 * Ohne Layout — jsdom liefert lauter Nullen — bleibt es bei `down`, der Lage,
 * in der die beiden Kanten überhaupt gezeichnet werden.
 */
export function stackedDirection(a: EdgeBox, b: EdgeBox): StackDir {
  const dy = b.cy - a.cy;
  const dx = b.cx - a.cx;
  if (Math.abs(dy) >= Math.abs(dx)) return dy < 0 ? "up" : "down";
  return dx < 0 ? "left" : "right";
}

export interface StackedPath {
  dir: StackDir;
  /** Der Schaft. */
  d: string;
  /** Die Pfeilspitze als geschlossenes Dreieck am Zielknoten. */
  arrow: string;
  /** Spitze des Pfeils — Endpunkt der Kante. */
  x: number;
  y: number;
}

/** Schaft + Pfeilspitze zwischen zwei Knoten derselben Bahn. */
export function stackedEdgePath(a: EdgeBox, b: EdgeBox): StackedPath {
  const dir = stackedDirection(a, b);
  if (dir === "down" || dir === "up") {
    const s = dir === "down" ? 1 : -1;
    const x = (a.left + b.left) / 2 + AXIS;
    const y1 = dir === "down" ? a.bottom + INSET : a.top - INSET;
    const y2 = dir === "down" ? b.top - INSET : b.bottom + INSET;
    return {
      dir,
      d: `M${f(x)} ${f(y1)} L${f(x)} ${f(y2)}`,
      arrow:
        `M${f(x - HALF)} ${f(y2 - s * HEAD)} L${f(x)} ${f(y2)} ` +
        `L${f(x + HALF)} ${f(y2 - s * HEAD)} Z`,
      x,
      y: y2,
    };
  }
  const s = dir === "right" ? 1 : -1;
  const y = (a.top + b.top) / 2 + AXIS;
  const x1 = dir === "right" ? a.right + INSET : a.left - INSET;
  const x2 = dir === "right" ? b.left - INSET : b.right + INSET;
  return {
    dir,
    d: `M${f(x1)} ${f(y)} L${f(x2)} ${f(y)}`,
    arrow:
      `M${f(x2 - s * HEAD)} ${f(y - HALF)} L${f(x2)} ${f(y)} ` +
      `L${f(x2 - s * HEAD)} ${f(y + HALF)} Z`,
    x: x2,
    y,
  };
}

/* ── Die Beziehung als Wort ───────────────────────────────────────────────── */

/**
 * Der Klartextname eines Knotens. Erst die Titelzeile, dann der Teil des
 * `aria-label` vor dem Gedankenstrich (`"Name — Zustand"`), zuletzt die ID.
 * Der `aria-label`-Weg trägt auch dann, wenn dort schon ein von uns angehängter
 * Zusatz steht — der Name bleibt der erste Abschnitt.
 */
export function flowNodeName(el: Element): string {
  const title = el.querySelector(".flow-node-title")?.textContent?.trim();
  if (title) return title;
  const label = el.getAttribute("aria-label") ?? "";
  const head = label.split(" — ")[0].trim();
  if (head) return head;
  return (el as HTMLElement).dataset?.flowNode ?? "";
}

/** „ · füllt X" bzw. „ · wird von X gefüllt" — Zusatz zum vorhandenen Namen. */
export function relationSuffix(fills: string[], filledBy: string[]): string {
  const parts: string[] = [];
  if (fills.length) parts.push(`füllt ${list(fills)}`);
  if (filledBy.length) parts.push(`wird von ${list(filledBy)} gefüllt`);
  return parts.length ? ` · ${parts.join(", ")}` : "";
}

function list(names: string[]): string {
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} und ${names[names.length - 1]}`;
}

/* ── Anbringen am DOM ─────────────────────────────────────────────────────── */

export interface UseFlowRelationsOptions {
  canvasRef: RefObject<HTMLElement | null>;
  edges: FlowEdgeSpec[];
  /** Auswahl der Knoten. Default `[data-flow-node]`. */
  selector?: string;
  /**
   * true = das Overlay zeichnet die bahninternen Kanten gerade selbst. Dann
   * trägt die Linie die Richtung und der CSS-Marker bleibt weg; sonst (Zoom
   * „Übersicht", gestapeltes Layout) ist der Marker die einzige Trägerin.
   */
  drawn?: boolean;
}

/**
 * Schreibt die bahninterne Beziehung an die beteiligten Knoten:
 * `aria-label` bekommt den Satz, der Knotenkasten ein `data-flow-fills` mit der
 * Richtung (Marker in `flow.css`).
 *
 * Warum imperativ und nicht als Prop an `FlowNode`: die Kantenliste kennt nur
 * das Overlay. Ein Durchreichen bis zum Knoten müsste durch jede aufrufende
 * Seite laufen — dieselbe Beziehung stünde dann in zwei Oberflächen zweimal und
 * könnte auseinanderlaufen. Der roving Tabindex der Startseite setzt seine
 * `tabIndex` aus demselben Grund am selben Ort.
 *
 * Der Effekt läuft in JEDEM Commit und ist idempotent: der zuletzt angehängte
 * Zusatz steht in `data-flow-rel`, wird vor dem Neuschreiben abgezogen und
 * überlebt so auch ein React-Update des `aria-label` (Standwechsel).
 */
export function useFlowRelations({
  canvasRef,
  edges,
  selector = "[data-flow-node]",
  drawn = false,
}: UseFlowRelationsOptions): void {
  const input = useRef({ edges, selector, drawn });
  input.current = { edges, selector, drawn };
  const touched = useRef<{ labels: HTMLElement[]; marks: HTMLElement[] }>({ labels: [], marks: [] });

  const apply = useCallback(() => {
    const canvas = canvasRef.current;
    const { edges: specs, selector: sel, drawn: isDrawn } = input.current;
    const labels: HTMLElement[] = [];
    const marks: HTMLElement[] = [];

    if (canvas) {
      const find = (id: string) =>
        canvas.querySelector<HTMLElement>(`${sel}[data-flow-node="${cssEscape(id)}"]`);
      const fills = new Map<HTMLElement, string[]>();
      const filledBy = new Map<HTMLElement, string[]>();
      const dirs = new Map<HTMLElement, StackDir>();

      for (const e of specs) {
        const a = find(e.from);
        const b = find(e.to);
        if (!a || !b || !isStackedPair(a, b)) continue;
        push(fills, a, flowNodeName(b));
        push(filledBy, b, flowNodeName(a));
        dirs.set(a, stackedDirection(boxOf(a), boxOf(b)));
      }

      for (const el of new Set([...fills.keys(), ...filledBy.keys()])) {
        if (setSuffix(el, relationSuffix(fills.get(el) ?? [], filledBy.get(el) ?? []))) labels.push(el);
      }
      if (!isDrawn) {
        for (const [el, dir] of dirs) {
          const box = el.closest(".flow-node");
          if (box instanceof HTMLElement) {
            box.dataset.flowFills = dir;
            marks.push(box);
          }
        }
      }
    }

    for (const el of touched.current.labels) if (!labels.includes(el)) setSuffix(el, "");
    for (const el of touched.current.marks) if (!marks.includes(el)) delete el.dataset.flowFills;
    touched.current = { labels, marks };
  }, [canvasRef]);

  useEffect(() => {
    apply();
  });

  /**
   * Ohne Overlay misst NIEMAND mehr (`useFlowEdges` hängt seinen Beobachter nur
   * bei `enabled` an). Die Richtung des Markers hängt aber an der Anordnung:
   * die Knotenspalten beider gestapelten Layouts sind `auto-fill`-Raster, die
   * beim Ziehen des Fensters von nebeneinander auf untereinander kippen, ohne
   * dass React etwas neu rendert. Deshalb hier ein eigener, schreibender (nicht
   * rendernder) Beobachter — er kann keine Schleife auslösen.
   */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", apply);
      return () => window.removeEventListener("resize", apply);
    }
    const ro = new ResizeObserver(() => apply());
    ro.observe(canvas);
    return () => ro.disconnect();
  }, [apply, canvasRef]);

  // Beim Abbauen bleibt sonst ein fremder Satz in einem fremden `aria-label`
  // stehen — die Knoten gehören der Seite, nicht dem Overlay.
  useEffect(
    () => () => {
      for (const el of touched.current.labels) setSuffix(el, "");
      for (const el of touched.current.marks) delete el.dataset.flowFills;
      touched.current = { labels: [], marks: [] };
    },
    [],
  );
}

function push(map: Map<HTMLElement, string[]>, key: HTMLElement, value: string) {
  const list = map.get(key);
  if (list) list.push(value);
  else map.set(key, [value]);
}

function boxOf(el: Element): EdgeBox {
  const r = el.getBoundingClientRect();
  return {
    left: r.left,
    right: r.right,
    top: r.top,
    bottom: r.bottom,
    cx: r.left + r.width / 2,
    cy: r.top + r.height / 2,
  };
}

/** Hängt den Zusatz an — oder nimmt ihn zurück. Rückgabe: steht jetzt einer da? */
function setSuffix(el: HTMLElement, suffix: string): boolean {
  const current = el.getAttribute("aria-label");
  if (current === null) return false;
  const prev = el.dataset.flowRel ?? "";
  const base = prev && current.endsWith(prev) ? current.slice(0, current.length - prev.length) : current;
  if (!suffix) {
    delete el.dataset.flowRel;
    if (base !== current) el.setAttribute("aria-label", base);
    return false;
  }
  el.dataset.flowRel = suffix;
  if (base + suffix !== current) el.setAttribute("aria-label", base + suffix);
  return true;
}

const f = (n: number) => n.toFixed(1);

function cssEscape(value: string): string {
  const g = globalThis as { CSS?: { escape?: (v: string) => string } };
  if (typeof g.CSS?.escape === "function") return g.CSS.escape(value);
  return value.replace(/["\\]/g, "\\$&");
}
